import React from "react";
import { useSeo } from "@/hooks/useSeo";
import { Link } from "react-router-dom";
import { useFacultyRoster } from "@/hooks/useFacultyRoster";
import { Mail, Linkedin, Award, ArrowRight, GraduationCap, Briefcase, Sparkles, BookMarked, Trophy } from "lucide-react";

const EXPERTISE = [
  "Converged Security Architecture",
  "Cyber-Physical Security",
  "Cybersecurity & Digital Resilience",
  "Governance, Risk & Compliance",
  "Digital Transformation Enablement",
  "Integrated Physical & Cyber Security",
  "Security Technology Solutions",
  "International Law & Security Governance",
  "Project & Programme Leadership",
  "Threat Intelligence & Incident Management",
  "Security Culture & Education",
  "Strategic Risk Management",
  "Lean Six Sigma for Security Operations",
];

const POSITIONS = [
  {
    title: "Chief Instructor & Managing Director, Converged Security Institute (CSI)",
    sub: "Converged Security & Digital Transformation Strategist",
    icon: Briefcase,
  },
  {
    title: "Distinguished Professor in IT & Cyber Security",
    sub: "QAHE & Pebble Hills University Distinguished Professorship (May 2026) · Certified Lecturer in Cybersecurity",
    icon: GraduationCap,
  },
  {
    title: "Dean of Cybersecurity Programme",
    sub: "Business Technology Academy, Ireland",
    icon: BookMarked,
  },
  {
    title: "Cyber-Physical Systems Security Expert",
    sub: "Nestlé - global enterprise cyber-physical security",
    icon: Sparkles,
  },
  {
    title: "Professor, IT OTHM Programmes",
    sub: "C3S Business School (until June 2026)",
    icon: GraduationCap,
  },
  {
    title: "GCSB leadership: President & Chair; Managing Director / CEO",
    sub: "Global Converged Security Board (GCSB), the independent accrediting body. These GCSB offices are separate from his CSI role as Chief Instructor & Managing Director; CSI has no role in GCSB standards, assessment, credential or Trustmark decisions.",
    icon: Award,
    href: "https://gcsb.uk/governance",
  },
];

const BADGES = {
  qahe: { src: "/badges/qahe-excellence-education-2026.png", alt: "QAHE Excellence in Education Award 2026-2027" },
  gcsb: { src: "/badges/gcsb-dfgcsb.webp", alt: "GCSB Distinguished Fellow Academic (DFGCSB)", href: "https://credsverse.com/credentials/aff2ad2e-c673-4f3a-82c1-b854fb8decd8" },
  cyber: { src: "/badges/cybersecurity-excellence-2026-winner.png", alt: "Cybersecurity Excellence Awards 2026 Winner" },
  t10: { src: "/badges/thinkers360-top10-architecture.png", alt: "Thinkers360 Top 10 Thought Leader - Architecture" },
  t25: { src: "/badges/thinkers360-top25-security.png", alt: "Thinkers360 Top 25 Thought Leader - Security" },
  t50: { src: "/badges/thinkers360-top50-it-leadership.png", alt: "Thinkers360 Top 50 Thought Leader - IT Leadership" },
};

const ACHIEVEMENTS = [
  {
    year: "2026 / 2027",
    title: "Excellence in Education Award - QAHE",
    desc: "Awarded the Excellence in Education Award 2026-2027 by the International QAHE Association (ISO 17011:2017 certified agency) for dedication to making sophisticated security concepts accessible to executives, practitioners and future leaders.",
    badges: ["qahe"],
  },
  {
    year: "2026",
    title: "Winner - Cybersecurity Excellence Awards",
    desc: "2026 winner in the Cybersecurity Excellence Awards, named Continuous Improvement & Optimization Expert of the Year.",
    badges: ["cyber"],
  },
  {
    year: "2026",
    title: "Thinkers360 Top Thought Leader",
    desc: "Ranked by Thinkers360 among the Top 10 Thought Leaders in Architecture, Top 25 in Security, Top 25 Global Thought Leaders in IT Strategy and Top 50 in IT Leadership.",
    badges: ["t10", "t25", "t50"],
  },
  {
    year: "2026",
    title: "Distinguished Professor in IT & Cyber Security",
    desc: "Awarded a Distinguished Professorship in IT & Cyber Security in May 2026 by the International QAHE Association in partnership with Pebble Hills University, recognising academic leadership and contribution to IT and cyber security education.",
  },
  {
    year: "2025",
    title: "Global Recognition & Industry Leadership",
    desc: "Recognised for strategic leadership, industry transformation, research excellence and education leadership in converged security at global forums and workshops.",
  },
  {
    year: "Ongoing",
    title: "Converged Security Thought Leadership",
    desc: "Author of publications on enterprise converged security systems, EU legislative frameworks, governance challenges and security for manufacturing environments.",
  },
];

export default function InstructorsPage() {
  useSeo({ title: "Faculty & Instructors", description: "Meet the CSI faculty - senior practitioners and academics in converged cyber, physical and OT security leadership.", path: "/instructors" });
  return (
    <div data-testid="instructors-page">
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-6">
        <div className="eyebrow mb-3">Leadership & Expertise</div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05]" data-testid="instructors-h1">
          Converged Security <span className="italic text-brand-gold">Leadership Profile</span>
        </h1>
        <p className="mt-4 text-sm sm:text-base text-zinc-700 max-w-3xl">
          Cybersecurity &amp; Digital Resilience Leader · Converged Security Strategist · Distinguished Professor · Industry Advisor
        </p>
      </section>

      {/* Profile */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 grid lg:grid-cols-12 gap-8 items-start">
        <aside className="lg:col-span-4">
          <div className="card-warm p-5 sticky top-24" data-testid="instructor-card-bunic">
            <div className="relative rounded-2xl overflow-hidden bg-[var(--navy-deep)] aspect-[4/5]">
              <img
                src="/instructors/vladimir-bunic.jpg"
                alt="Prof. Dr. Vladimir Bunic - Chief Instructor & Managing Director, Converged Security Institute"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/70 via-black/30 to-transparent">
                <div className="text-[10px] font-bold tracking-widest uppercase text-[var(--gold-soft)]">Chief Instructor & Managing Director</div>
                <div className="font-display text-lg text-white leading-tight mt-0.5">Prof. Dr. Vladimir Bunic</div>
              </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <a href="https://www.linkedin.com/in/vladimir-bunic/" target="_blank" rel="noreferrer"
                 className="chip inline-flex items-center gap-1.5" data-testid="bunic-linkedin">
                <Linkedin size={13}/> LinkedIn
              </a>
              <a href="mailto:info@convergedsecurity.es?subject=CSI%20Faculty%20Enquiry"
                 className="chip inline-flex items-center gap-1.5" data-testid="bunic-email">
                <Mail size={13}/> Contact
              </a>
            </div>
            <div className="mt-4 pt-4 border-t border-[var(--border-warm)] text-xs text-zinc-700 space-y-1.5">
              <div className="flex gap-2"><Award size={13} className="text-[var(--gold-deep)] mt-0.5 shrink-0"/><span>D.Sc. in Cyber-Physical Security</span></div>
              <div className="flex gap-2"><Award size={13} className="text-[var(--gold-deep)] mt-0.5 shrink-0"/><span>PhD in Cybersecurity</span></div>
              <div className="flex gap-2"><Award size={13} className="text-[var(--gold-deep)] mt-0.5 shrink-0"/><span>DBA in General Management</span></div>
              <div className="flex gap-2"><Award size={13} className="text-[var(--gold-deep)] mt-0.5 shrink-0"/><span>PG studies: Digital Transformation, International Law, Project Management</span></div>
            </div>
            <div className="mt-4 pt-4 border-t border-[var(--border-warm)]" data-testid="bunic-badges">
              <div className="eyebrow mb-2">Awards &amp; recognition</div>
              <div className="grid grid-cols-5 gap-1.5 items-center">
                {["qahe", "gcsb", "cyber", "t10", "t25", "t50"].map((k) => {
                  const img = <img src={BADGES[k].src} alt={BADGES[k].alt} title={BADGES[k].alt} loading="lazy"
                       className={`w-full object-contain rounded-lg bg-white p-1 ${k === "qahe" ? "h-20" : "h-16"}`} data-testid={`badge-${k}`}/>;
                  const cls = k === "qahe" ? "col-span-5" : "";
                  return BADGES[k].href
                    ? <a key={k} href={BADGES[k].href} target="_blank" rel="noreferrer" className={cls}>{img}</a>
                    : <div key={k} className={cls}>{img}</div>;
                })}
              </div>
            </div>
          </div>
        </aside>

        <div className="lg:col-span-8 min-w-0 space-y-6">
          <div>
            <div className="eyebrow mb-2">Prof. Dr. Vladimir Bunic</div>
            <h2 className="font-display text-3xl sm:text-4xl leading-tight">Chief Instructor &amp; Managing Director, Converged Security Institute</h2>
          </div>

          <div className="card-warm p-6 space-y-4 text-sm sm:text-base text-zinc-800 leading-relaxed">
            <p>
              With over two decades at the intersection of cyber, physical and digital security, Prof. Dr. Vladimir Bunic specialises in
              designing integrated security architectures that strengthen organisational resilience and enable confident decision-making
              in complex environments. His work blends deep technical expertise with strategic clarity, ensuring organisations stay ahead
              of evolving threats.
            </p>
            <p>
              He holds a <b>Doctor of Science (D.Sc) in Cyber-Physical Security</b>, a <b>PhD in Cybersecurity</b> and a <b>DBA in General
              Management</b>, supported by postgraduate studies in Digital Transformation, International Law and Project Management. This
              multidisciplinary foundation allows him to bridge executive strategy with advanced security engineering.
            </p>
            <p>
              As a <b>QAHE Distinguished Professor in IT &amp; Cyber Security</b> and a <b>Certified Lecturer in Cybersecurity</b>, he is
              committed to making sophisticated security concepts accessible for executives, practitioners and future leaders. His dedication
              to education has been recognised through the <b>Excellence in Education Award 2026-2027</b>.
            </p>
            <p>
              His industry contributions have also earned global recognition, including the <b>Cybersecurity Excellence Award - Continuous
              Improvement &amp; Optimization Expert of the Year (2026 Winner)</b>, highlighting his impact on advancing security standards and
              operational resilience.
            </p>
            <p>
              Applying Lean Six Sigma methods to optimise security operations across consulting, academia and leadership roles, Prof. Dr. Bunic collaborates with multidisciplinary teams to identify
              vulnerabilities, enhance cyber-physical systems and align security initiatives with business value. He remains actively
              engaged in global forums and workshops, advocating for stronger security cultures and driving forward the evolution of
              digital resilience.
            </p>
          </div>
        </div>
      </section>

      {/* Roles */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="eyebrow mb-1">Current &amp; Past Positions</div>
        <h2 className="font-display text-3xl sm:text-4xl mb-6">Roles &amp; Positions</h2>
        <div className="grid md:grid-cols-2 gap-4">
          {POSITIONS.map((p, i) => (
            <div key={i} className="card-warm p-5 flex items-start gap-3" data-testid={`position-${i}`}>
              <div className="w-10 h-10 rounded-full bg-[var(--navy)] text-[var(--gold-soft)] grid place-items-center shrink-0">
                <p.icon size={18}/>
              </div>
              <div>
                <h3 className="font-display text-lg leading-snug">{p.title}</h3>
                <p className="text-xs text-zinc-600 mt-1">{p.sub}</p>
                {p.href && <a href={p.href} target="_blank" rel="noreferrer" className="text-xs font-semibold text-[var(--terracotta)] hover:underline inline-block mt-1" data-testid={`position-link-${i}`}>View GCSB governance ↗</a>}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Expertise */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="eyebrow mb-1">Specialisations</div>
        <h2 className="font-display text-3xl sm:text-4xl mb-6">Areas of Expertise</h2>
        <div className="flex flex-wrap gap-2">
          {EXPERTISE.map((e) => (
            <span key={e}
                  className="inline-flex items-center gap-1 rounded-full border border-[var(--border-warm)] bg-white px-3 py-1.5 text-xs font-semibold text-[var(--navy)]"
                  data-testid={`expertise-${e.replace(/\s+/g, '-').toLowerCase()}`}>
              {e}
            </span>
          ))}
        </div>
      </section>

      {/* Achievements */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="eyebrow mb-1">Career Highlights</div>
        <h2 className="font-display text-3xl sm:text-4xl mb-6">Achievements &amp; Leadership</h2>
        <div className="relative">
          <div className="absolute left-[11px] top-2 bottom-2 w-px bg-[var(--border-warm)]" aria-hidden="true"/>
          <ol className="space-y-5">
            {ACHIEVEMENTS.map((a, i) => (
              <li key={i} className="relative pl-10" data-testid={`achievement-${i}`}>
                <span className="absolute left-0 top-1 w-6 h-6 rounded-full bg-[var(--gold-deep)] text-white grid place-items-center shadow">
                  <Trophy size={13}/>
                </span>
                <div className="text-[11px] font-bold tracking-widest uppercase text-[var(--gold-deep)]">{a.year}</div>
                <h3 className="font-display text-xl leading-tight mt-0.5">{a.title}</h3>
                <p className="text-sm text-zinc-700 mt-1 leading-relaxed max-w-3xl">{a.desc}</p>
                {a.badges && (
                  <div className="flex flex-wrap gap-3 mt-3">
                    {a.badges.map((k) => (
                      <img key={k} src={BADGES[k].src} alt={BADGES[k].alt} title={BADGES[k].alt} loading="lazy"
                           className={`object-contain rounded-lg bg-white border border-[var(--border-warm)] p-1.5 ${k === "qahe" ? "h-20 w-auto" : "h-24 w-auto"}`}/>
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ol>
        </div>
      </section>

      <ApprovedFaculty/>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="rounded-3xl bg-[var(--navy-deep)] text-white p-8 sm:p-12 text-center">
          <div className="eyebrow text-[var(--gold-soft)]">Work with CSI</div>
          <h3 className="font-display text-3xl sm:text-4xl mt-2">Engage with Our Expertise</h3>
          <p className="text-zinc-300 max-w-2xl mx-auto mt-3 text-sm sm:text-base">
            Interested in collaboration, speaking engagements, consulting or training enquiries? Reach out to the Converged Security Institute.
          </p>
          <div className="mt-6 flex items-center justify-center gap-3 flex-wrap">
            <a href="mailto:info@convergedsecurity.es?subject=CSI%20Enquiry"
               className="btn-terracotta px-5 py-3 rounded-full text-sm font-semibold inline-flex items-center gap-2"
               data-testid="instructors-contact-btn">
              <Mail size={14}/> Contact us
            </a>
            <Link to="/join-faculty"
                  className="chip px-5 py-3 bg-white/5 border-white/20 text-white hover:bg-white/10 inline-flex items-center gap-2"
                  data-testid="instructors-join-faculty-btn">
              Apply to join the faculty
            </Link>
            <Link to="/education"
                  className="chip px-5 py-3 bg-white/5 border-white/20 text-white hover:bg-white/10 inline-flex items-center gap-2"
                  data-testid="instructors-education-btn">
              View Programmes <ArrowRight size={14}/>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function ApprovedFaculty() {
  const roster = useFacultyRoster().filter((f) => f.type !== "chief");
  if (!roster.length) return null;
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="approved-faculty">
      <div className="eyebrow mb-2">Faculty</div>
      <h2 className="font-display text-3xl mb-5">Guest &amp; adjunct faculty</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {roster.map((f) => (
          <article key={f.slug} className="card-warm p-5" data-testid={`faculty-member-${f.slug}`}>
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 rounded-full overflow-hidden bg-[var(--navy-deep)] shrink-0 grid place-items-center text-[var(--gold-soft)] font-display">
                {f.image ? <img src={f.image} alt={f.name} className="w-full h-full object-cover" loading="lazy"/> : f.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}
              </div>
              <div className="min-w-0">
                <div className="font-display text-lg leading-tight">{f.name}</div>
                <div className="text-xs text-zinc-600">{f.title}</div>
              </div>
            </div>
            <p className="text-sm text-zinc-700 mt-3">{f.blurb}</p>
            {f.linkedin && <a href={f.linkedin} target="_blank" rel="noreferrer" className="chip inline-flex items-center gap-1 text-xs mt-3"><Linkedin size={12}/> LinkedIn</a>}
          </article>
        ))}
      </div>
    </section>
  );
}
